import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../../shared/api/client';
import type { BiblePassage } from '../../shared/api/types';
import { Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { BibleTabs } from './Atlas';
import { bibleLink } from './BiblePage';

type TopicSummary = { id: string; name: string; refs: number };
type TopicItem = { text: string; heading: boolean; see: { id: string; name: string } | null; refs: string[] };
const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const FEATURED = ['Amor', 'Fe', 'Oración', 'Perdón', 'Esperanza', 'Paz', 'Gozo', 'Temor', 'Familia', 'Matrimonio', 'Niños e hijos', 'Salvación', 'Arrepentimiento', 'Humildad', 'Sabiduría', 'Obediencia', 'Aflicciones y adversidades', 'Gratitud', 'Generosidad', 'Diezmos'];

function Credit({ text }: { text?: string }) {
  return <p className="small muted atlas__credit">{text ?? "Índice por temas: Nave's Topical Bible (Orville J. Nave, 1896, dominio público). Traducción y adaptación al español: KEORA."}</p>;
}

export function TopicsPage() {
  const list = useAsync((signal) => api.get<{ items: TopicSummary[]; attribution: string }>('/bible/topics', signal), []);
  const [q, setQ] = useState('');
  const items = list.status === 'ready' ? list.data.items : [];
  const shown = useMemo(() => (q.trim() ? items.filter((t) => norm(t.name).includes(norm(q.trim()))) : []), [q, items]);
  const featured = FEATURED.map((n) => items.find((t) => t.name === n)).filter(Boolean) as TopicSummary[];
  return (
    <div className="page page--narrow bible-wide">
      <header className="page__header">
        <h1>Biblia</h1>
      </header>
      <BibleTabs active="temas" />
      <Card>
        <Field label="Buscar un tema" name="tema" value={q} onChange={(e) => setQ(e.target.value)} placeholder="amor, perdón, ansiedad, matrimonio…" autoComplete="off" />
      </Card>
      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {q.trim() ? (
        shown.length ? (
          <Card title={`Temas (${shown.length})`}>
            <ul className="list">
              {shown.slice(0, 100).map((t) => (
                <li key={t.id} className="list__item">
                  <Link to={`/app/biblia/temas/${t.id}`}>{t.name}</Link> <span className="muted small">· {t.refs} citas</span>
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <EmptyState title="No encontramos ese tema todavía." />
        )
      ) : list.status === 'ready' ? (
        <>
          {featured.length ? (
            <Card title="Temas frecuentes">
              <div className="chips">
                {featured.map((t) => (
                  <Link key={t.id} className="chip" to={`/app/biblia/temas/${t.id}`}>
                    {t.name}
                  </Link>
                ))}
              </div>
            </Card>
          ) : null}
          <Card title={`Todos los temas (${items.length})`}>
            <ul className="list topic-index">
              {items.map((t) => (
                <li key={t.id}>
                  <Link to={`/app/biblia/temas/${t.id}`}>{t.name}</Link>
                </li>
              ))}
            </ul>
          </Card>
        </>
      ) : null}
      <Credit />
    </div>
  );
}

/** Muestra el texto de las primeras citas al abrir un subtema. */
function RefTexts({ refs }: { refs: string[] }) {
  const data = useAsync(
    (signal) => Promise.all(refs.slice(0, 5).map((r) => api.get<BiblePassage>(`/bible/passage?ref=${encodeURIComponent(r)}`, signal).catch(() => null))),
    [refs.join('|')],
  );
  if (data.status !== 'ready') return <Loading />;
  return (
    <ul className="topic-texts">
      {data.data.map((p, i) =>
        p && p.verses.length ? (
          <li key={i}>
            <Link to={bibleLink(p.label)}>
              <strong>{p.label}</strong>
            </Link>{' '}
            {p.verses
              .slice(0, 6)
              .map((v) => v.text)
              .join(' ')}
            {p.verses.length > 6 ? ' …' : ''}
          </li>
        ) : null,
      )}
    </ul>
  );
}

function TopicItemRow({ item }: { item: TopicItem }) {
  const [open, setOpen] = useState(false);
  if (item.heading && item.refs.length === 0 && !item.see) return <li className="topic-item topic-item--heading">{item.text}</li>;
  return (
    <li className={`topic-item${item.heading ? ' topic-item--heading' : ''}`}>
      {item.see ? <Link to={`/app/biblia/temas/${item.see.id}`}>{item.text}</Link> : <span>{item.text}</span>}
      {item.refs.length ? (
        <p className="topic-item__refs">
          {item.refs.map((r, i) => (
            <span key={r + i}>
              {i ? '; ' : ''}
              <Link to={bibleLink(r)}>{r}</Link>
            </span>
          ))}
        </p>
      ) : null}
      {item.refs.length ? (
        <>
          <button type="button" className="btn btn--ghost topic-item__toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? 'Ocultar textos' : 'Leer los textos'}
          </button>
          {open ? <RefTexts refs={item.refs} /> : null}
        </>
      ) : null}
    </li>
  );
}

export function TopicPage() {
  const { topicId = '' } = useParams();
  const data = useAsync((signal) => api.get<{ id: string; name: string; items: TopicItem[]; attribution: string }>(`/bible/topics/${topicId}`, signal), [topicId]);
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const t = data.data;
  return (
    <div className="page page--narrow bible-wide">
      <p className="small">
        <Link to="/app/biblia/temas">← Temas</Link>
      </p>
      <header className="page__header">
        <h1>{t.name}</h1>
      </header>
      <Card>
        <ul className="topic-items">
          {t.items.map((i, k) => (
            <TopicItemRow key={k} item={i} />
          ))}
        </ul>
      </Card>
      <Credit text={t.attribution} />
    </div>
  );
}
